import { useCallback, useEffect, useState } from "react";
import { ArrowUp, ArrowDown, FileDown, Loader2, Layers, X } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, downloadBlob } from "../lib/api";
import { money, xx } from "../lib/casePath";
import { Btn, Card } from "../components/kit/Kit";
import { AIProgress } from "../components/kit/AIProgress";

const inp = "rounded-xl border border-[#E2DDD5] bg-[#FAF8F5] px-4 py-2.5 text-sm outline-none focus:border-[#E8643C] focus:bg-white";
const today = () => new Date().toISOString().slice(0, 10);

const Paquetes = ({ items }) => (
  <Card title="Paquetes generados" testid="comite-paquetes">
    {items.length === 0 && <p className="text-sm text-[#8C867A]">Aún no hay paquetes.</p>}
    <div className="space-y-3">{items.map((p) => {
      const run = ["queued", "running"].includes(p.job?.status);
      return (
        <div key={p.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-[#E2DDD5] p-4" data-testid={`paquete-${p.id}`}>
          <div className="min-w-0 flex-1"><p className="font-semibold">{p.nombre}</p>
            <p className="text-sm text-[#8C867A]">Sesión {p.fecha_sesion} · {p.totales.n} expedientes · {money(p.totales.monto)}{p.paginas ? ` · ${p.paginas} págs.` : ""}</p>
            {run && <div className="mt-2 max-w-md" data-testid={`paquete-progress-${p.id}`}><AIProgress job={p.job} title="Paquete de Comité" compact testid={`paquete-ai-${p.id}`} /></div>}
            {p.job?.status === "error" && <p className="mt-1 text-xs text-rose-700">Error: {p.job.message}</p>}
          </div>
          <Btn kind="secondary" className="px-3.5 py-2" disabled={!p.pdf_storage_path || run} data-testid={`paquete-pdf-${p.id}`}
            onClick={() => downloadBlob(`/comite/paquetes/${p.id}/pdf`, `Comite_${p.nombre.slice(0, 30)}.pdf`)}>
            {run ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} {run ? "Generando…" : "Descargar PDF"}
          </Btn>
        </div>
      );
    })}</div>
  </Card>
);

export default function Comite() {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [cands, setCands] = useState([]);
  const [sel, setSel] = useState([]);
  const [nombre, setNombre] = useState("Sesión ordinaria de Comité de Crédito");
  const [fecha, setFecha] = useState(today());
  const [paqs, setPaqs] = useState([]);
  const loadP = useCallback(() => http.get("/comite/paquetes").then((r) => setPaqs(r.data)), []);
  useEffect(() => { http.get("/comite/candidatos", { params: { desde, hasta } }).then((r) => setCands(r.data)); }, [desde, hasta]);
  useEffect(() => { loadP(); }, [loadP]);
  useEffect(() => {
    if (!paqs.some((p) => ["queued", "running"].includes(p.job?.status))) return undefined;
    const t = setTimeout(loadP, 2500);
    return () => clearTimeout(t);
  }, [paqs, loadP]);

  const toggle = (c) => setSel((s) => (s.some((x) => x.expediente_id === c.expediente_id) ? s.filter((x) => x.expediente_id !== c.expediente_id) : [...s, c]));
  const move = (i, d) => setSel((s) => { const n = [...s]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const gen = async () => {
    try {
      await http.post("/comite/paquetes", { nombre, fecha_sesion: fecha, expediente_ids: sel.map((x) => x.expediente_id) });
      toast.success("Generando paquete de Comité…");
      loadP();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const total = sel.reduce((s, x) => s + (x.monto || 0), 0);

  return (
    <main className="mx-auto max-w-7xl px-6 py-10 lg:px-10" data-testid="comite-page">
      <p className="text-xs font-semibold uppercase tracking-widest text-[#8C867A]">Solo Director de Crédito</p>
      <h1 className="font-serif-display mt-2 text-4xl font-bold sm:text-5xl">Comité<span className="text-[#E8643C]">.</span></h1>
      <div className="mt-8 grid gap-8 lg:grid-cols-12">
        <Card title="Expedientes aprobados" subtitle="Selecciona los que entran a la sesión." className="lg:col-span-7" testid="comite-candidatos"
          right={<div className="flex flex-wrap items-center gap-2 text-xs text-[#8C867A]">Aprobados del
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className={inp} data-testid="comite-desde" /> al
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className={inp} data-testid="comite-hasta" /></div>}>
          {cands.length === 0 && <p className="text-sm text-[#8C867A]" data-testid="comite-sin-candidatos">No hay expedientes aprobados en el rango.</p>}
          <div className="space-y-2">{cands.map((c) => {
            const on = sel.some((x) => x.expediente_id === c.expediente_id);
            return (
              <label key={c.expediente_id} className={`flex cursor-pointer items-center gap-4 rounded-2xl border px-4 py-3 transition-colors ${on ? "border-[#E8643C] bg-[#FDF0EB]" : "border-[#E2DDD5] hover:bg-[#FAF8F5]"}`} data-testid={`candidato-${c.expediente_id}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(c)} className="h-4 w-4 accent-[#E8643C]" data-testid={`candidato-check-${c.expediente_id}`} disabled={!c.pdf_ready} />
                <div className="min-w-0 flex-1"><p className="truncate font-semibold">{c.nombre}</p>
                  <p className="text-xs text-[#8C867A]">{c.rfc} · v{c.version} · {c.analista} · aprobado {c.aprobado_at?.slice(0, 10)}{!c.pdf_ready && " · PDF generándose"}</p></div>
                <div className="text-right text-sm"><p className="font-semibold tabular-nums">{money(c.monto)}</p><p className="text-xs text-[#8C867A]">{c.score}/{c.band} · DSCR {xx(c.dscr)}</p></div>
              </label>
            );
          })}</div>
        </Card>
        <Card title="Sesión" className="lg:col-span-5" testid="comite-sesion">
          <label className="block text-xs font-semibold uppercase tracking-widest text-[#8C867A]">Nombre de la sesión
            <input value={nombre} onChange={(e) => setNombre(e.target.value)} className={`${inp} mt-1.5 w-full`} data-testid="comite-nombre" /></label>
          <label className="mt-4 block text-xs font-semibold uppercase tracking-widest text-[#8C867A]">Fecha de sesión
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={`${inp} mt-1.5 w-full`} data-testid="comite-fecha" /></label>
          <p className="mt-6 text-xs font-semibold uppercase tracking-widest text-[#8C867A]">Orden del día · {sel.length} · {money(total)}</p>
          <ol className="mt-2 space-y-2" data-testid="comite-orden">{sel.map((x, i) => (
            <li key={x.expediente_id} className="flex items-center gap-2 rounded-xl bg-[#FAF8F5] px-3 py-2 text-sm" data-testid={`orden-${i}`}>
              <span className="w-5 font-bold text-[#E8643C]">{i + 1}</span><span className="min-w-0 flex-1 truncate">{x.nombre}</span>
              <button disabled={i === 0} onClick={() => move(i, -1)} className="rounded p-1 hover:bg-white disabled:opacity-30" data-testid={`orden-up-${i}`}><ArrowUp size={14} /></button>
              <button disabled={i === sel.length - 1} onClick={() => move(i, 1)} className="rounded p-1 hover:bg-white disabled:opacity-30" data-testid={`orden-down-${i}`}><ArrowDown size={14} /></button>
              <button onClick={() => toggle(x)} className="rounded p-1 hover:bg-white" data-testid={`orden-remove-${i}`}><X size={14} /></button>
            </li>))}</ol>
          <Btn kind="accent" className="mt-6 w-full" disabled={!sel.length || nombre.trim().length < 3} onClick={gen} data-testid="comite-generar-button"><Layers size={16} /> Generar paquete PDF</Btn>
        </Card>
      </div>
      <div className="mt-8"><Paquetes items={paqs} /></div>
    </main>
  );
}
